import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Observable, Subject, concatMap, filter, map, merge, of, take, takeUntil, timer } from 'rxjs';

@Component({
  selector: 'app-c-elements-marqueurs',
  templateUrl: './c-elements-marqueurs.component.html',
  styleUrls: ['./c-elements-marqueurs.component.scss'],
})
export class CElementsMarqueursComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({ required: true }) startingDatas!: { x: number | string; y: number | string; label: string; description: string }[];
  @Input() outsideDatasUpdate$: Observable<any> | undefined;
  @Input() addMarqueurTrigger$: Subject<string> | undefined;
  @Input() deleteMarqueursTrigger$: Subject<any> | undefined;
  @Input({ required: true }) resizedWindowTrigger$!: Subject<any>;
  @Input({ required: true }) cannotMoveMarkers!: boolean;
  @Input({ required: false }) canClickMarkers: boolean = false;
  @Input({ required: true }) marqueurSize!: number;
  @Input() limitNbMarqueur: number | undefined;
  @Output() communicateFinalDatas = new EventEmitter<{ x: number; y: number; label: string }[]>();
  @Output() markerClicked = new EventEmitter<number>();
  @ViewChild('ccontainer', { static: false }) cContainer!: ElementRef;
  @Input() marqueursForms: FormArray<FormGroup> | undefined;

  finalDatas: { x: number; y: number; label: string; description: string }[] = [];

  initialDragPositions: { x: number; y: number; label: string; description: string }[] = [];

  finalDragPositions: { x: number; y: number; label: string; description: string }[] = [];

  containerWidth: number | undefined;
  containerHeight: number | undefined;

  destroyComponent$ = new Subject();

  constructor(
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.finalDatas = this.startingDatas.map(singleElement => ({x: Number(singleElement.x), y: Number(singleElement.y), label: singleElement.label, description: singleElement.description || ''}));
    if (this.addMarqueurTrigger$ !== undefined) {
      this.addMarqueurTrigger$.pipe(
        takeUntil(this.destroyComponent$)
      ).subscribe(e => this.addMarqueur(e));
    }
    if (this.deleteMarqueursTrigger$ !== undefined) {
      this.deleteMarqueursTrigger$.pipe(
        takeUntil(this.destroyComponent$)
      ).subscribe(_ => this.deleteMarqueurs());
    }
    if (this.outsideDatasUpdate$ !== undefined) {
      this.outsideDatasUpdate$.pipe(
        takeUntil(this.destroyComponent$)
      ).subscribe(e => {
        this.finalDatas = e;
        this.reevaluateCoordinatesinPixel();
      })
    }
  }

  ngAfterViewInit() {
    timer(0, 100).pipe(
      map(_ => this.cContainer),
      filter(_ => _ !== undefined),
      map(_ => this.getDimensions(_)),
      filter(({width, height}) => typeof width === 'number' && width !== 0 && typeof height === 'number' && height !== 0),
      take(1),
      concatMap(_ => merge(
        of(1),
        this.resizedWindowTrigger$
      )),
      takeUntil(this.destroyComponent$)
    ).subscribe(_ => {
      this.updateWidthHeightAndReevaluateCoordinates();
    })
  }

  ngOnDestroy() {
    this.destroyComponent$.next(true);
  }


  getDimensions(element: ElementRef) {
    const width = element.nativeElement.offsetWidth;
    const height = element.nativeElement.offsetHeight;
    return {width, height};
  }

  updateWidthHeightAndReevaluateCoordinates() {
    const width = this.cContainer.nativeElement.offsetWidth;
    const height = this.cContainer.nativeElement.offsetHeight;
    this.containerWidth = width;
    this.containerHeight = height;
    requestAnimationFrame(() => {
      this.reevaluateCoordinatesinPixel();
    })
  }

  addMarqueur(label: string) {
    if (this.limitNbMarqueur === undefined || this.limitNbMarqueur > this.initialDragPositions.length) {
      this.initialDragPositions.push({ x: 0, y: 0, label, description: '' });
      this.finalDragPositions.push({ x: 0, y: 0, label, description: '' });
      this.reevaluateFinalDatas();
    }
  }

  deleteMarqueurs() {
    this.initialDragPositions = [];
    this.finalDragPositions = [];
    this.reevaluateFinalDatas();
  }

  drop(data: { x: number; y: number; label: string, description: string }, index: number, $event: any) {
    const element = $event.source.element.nativeElement;
    const transformStyleProperty = element.style.getPropertyValue('transform')
    const regExpg = /translate3d\((-?\d+)px, (-?\d+)px, -?\d+px\)/g
    const regExp = /translate3d\((-?\d+)px, (-?\d+)px, -?\d+px\)/
    const match = transformStyleProperty.match(regExpg);
    const futureValues = ((match || []) as string[]).reduce<{ x: number, y: number, label: string, description: string }>((acc, e) => {
      const localMatch = e.match(regExp);
      if (localMatch !== null) {
        return {
          x: acc.x + Number(localMatch[1]),
          y: acc.y + Number(localMatch[2]),
          label: data.label,
          description: data.description,
        }
      }
      return acc;
    }, { x: 0, y: 0, label: data.label, description: data.description })
    this.finalDragPositions[index] = futureValues;
    this.reevaluateFinalDatas();
  }

  onMarkerClick(index: number) {
    this.markerClicked.emit(index);
  }

  reevaluateFinalDatas() {
    if (this.containerWidth && this.containerHeight) {
      const mapFunction = (width: number, height: number) => (val: { x: number; y: number; label: string, description: string }) => ({
        x: Number((val.x / (width - this.marqueurSize)).toFixed(4)),
        y: Number((val.y / (height - this.marqueurSize)).toFixed(4)),
        label: val.label,
        description: val.description,
      })
      this.finalDatas = this.finalDragPositions.map(mapFunction(this.containerWidth, this.containerHeight));
      this.communicateFinalDatas.emit(this.finalDatas);
    }
  }

  reevaluateCoordinatesinPixel() {
    if (this.containerWidth && this.containerHeight) {
      const mapFunction = (width: number, height: number) => (coords: { x: number; y: number; label: string, description: string }) => ({
        x: Math.round(coords.x * (width - this.marqueurSize)),
        y: Math.round(coords.y * (height - this.marqueurSize)),
        label: coords.label,
        description: coords.description,
      });
      this.initialDragPositions = this.finalDatas.map(mapFunction(this.containerWidth, this.containerHeight));
      this.finalDragPositions = [...this.initialDragPositions];
      this.changeDetectorRef.detectChanges();
    }
  }
}

@Pipe({
  name: 'marqueurformValidity'
})
export class MarqueurformValidityPipe implements PipeTransform {
  transform(marqueursForms: FormArray<FormGroup> | undefined, index: number, status: 'valid' | 'invalid'): boolean {
    if (marqueursForms instanceof FormArray) {
      const formGroup = marqueursForms.controls[index];
      if (formGroup instanceof FormGroup) {
        if (status === 'valid' && formGroup.valid) {
          return true;
        } else if (status === 'invalid' && formGroup.invalid) {
          return true;
        }
      }
    }
    return false
  }
}

@Pipe({
  name: 'dynamicMarqueurFontSize'
})
export class DynamicMarqueurFontSizePipe implements PipeTransform {
  transform(length: number): number {
    switch (length) {
      case 1:
        return 4 * length;
      case 2:
        return (16 / 5) * length;
      case 3:
        return (16 / 5) * length;
      default: return (16 / 5) * length;
    }

  }
}
